import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import Image from '@/components/atoms/Image';
import { CollapsibleSection } from '@/components/atoms/legacy/CollapsibleSection/CollapsibleSection';
import ColorCard from '@/components/molecules/ColorCard/ColorCard';
import ColorOption from '@/components/molecules/ColorOption/ColorOption';
import ExtendedOptionCard from '@/components/molecules/ExtendedOptionsCard/ExtendedOptionsCard';
import IncentivesOverlay from '@/components/molecules/IncentivesOverlay/IncentivesOverlay';
import { NoImageWrapper } from '@/components/molecules/NoImageWrapper/NoImageWrapper';
import TrimCard from '@/components/molecules/TrimCard/TrimCard';
import {
  FallBackColor,
  getDefaultImage,
  getRange,
} from '@/components/organisms/ChooseTrimForm/helpers';
import { CustomOrderCardProps } from '@/components/organisms/CustomOrderListingCard/CustomOrderListingCard';
import { ImageCarouselPopup } from '@/components/organisms/ImageCarouselPopUp/ImageCarouselPopup';
import { useSession } from '@/context/SavedVehicles';
import { useZipContext } from '@/context/ZipContext';
import { useFetchIncentives } from '@/hooks/queries/useFetchIncentives';
import {
  GOOGLE_TRACK_INFO,
  POPULAR_OPTIONS,
  PRICE_COMING_SOON,
} from '@/lib/constants';
import { priceFormatter } from '@/lib/priceFormatter';
import {
  ColorObject,
  CustomTrimDetail,
  ExtendedOptions,
  ExtendedOptionsFields,
  ImageObject,
  TrimStyle,
} from '@/lib/schema/build/types';
import { TrimFormState } from '@/types/pageStates';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import {
  FC,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { v4 } from 'uuid';

export interface ChooseTrimFormProps {
  trimStyles: TrimStyle[];
  build: CustomOrderCardProps | null;
  initialState: Partial<CustomTrimDetail> | null;
}

const SELECT_EXTERIOR = 'SELECT_EXTERIOR';
const SELECT_INTERIOR = 'SELECT_INTERIOR';
const SELECT_IMAGE = 'SELECT_IMAGE';
const ADD_EXTENDED_OPTION = 'ADD_EXTENDED_OPTION';
const REMOVE_EXTENDED_OPTION = 'REMOVE_EXTENDED_OPTION';
const CLEAR_EXTENDED_OPTION = 'CLEAR_EXTENDED_OPTION';
const SET_LOADING_IMAGE = 'SET_LOADING_IMAGE';
const SET_OPEN_INCENTIVES_OVERLAY = 'SET_OPEN_INCENTIVES_OVERLAY';
const SET_SELECTED_TRIM = 'SET_SELECTED_TRIM';

type ComponentState = {
  selectedTrim?: TrimStyle;
  selectedExteriorColor: ImageObject;
  selectedInteriorColor: ColorObject;
  selectedImage?: ImageObject['images'][0];
  selectedExtendedOptions: ExtendedOptionsFields[];
  openIncentivesOverlay: boolean;
  loadingImage: boolean;
};
type UpdateSelectedExteriorAction = {
  type: typeof SELECT_EXTERIOR;
  val: ImageObject;
};

type UpdateSelectedInteriorAction = {
  type: typeof SELECT_INTERIOR;
  val: ColorObject;
};

type UpdateSelectedImageAction = {
  type: typeof SELECT_IMAGE;
  val: ImageObject['images'][0] | undefined;
};

type UpdateSelectedExtendedOptionsAction = {
  type: typeof ADD_EXTENDED_OPTION;
  val: ExtendedOptionsFields;
};

type RemoveExtendedOptionAction = {
  type: typeof REMOVE_EXTENDED_OPTION;
  val: ExtendedOptionsFields;
};

type ClearExtendedOptionAction = {
  type: typeof CLEAR_EXTENDED_OPTION;
};

type SetLoadingImageAction = {
  type: typeof SET_LOADING_IMAGE;
  val: boolean;
};

type SetOpenIncentivesOverlayAction = {
  type: typeof SET_OPEN_INCENTIVES_OVERLAY;
  val: boolean;
};

type SetSelectedTrimAction = {
  type: typeof SET_SELECTED_TRIM;
  val: TrimStyle;
};

type ComponentAction =
  | UpdateSelectedExteriorAction
  | UpdateSelectedInteriorAction
  | UpdateSelectedImageAction
  | UpdateSelectedExtendedOptionsAction
  | RemoveExtendedOptionAction
  | ClearExtendedOptionAction
  | SetLoadingImageAction
  | SetOpenIncentivesOverlayAction
  | SetSelectedTrimAction;

type ComponentReducer = Reducer<ComponentState, ComponentAction>;

export const ChooseTrimForm: FC<ChooseTrimFormProps> = ({
  trimStyles,
  build,
  initialState,
}) => {
  const router = useRouter();
  const { saveSession } = useSession();
  const [sessionId] = useState((router.query.sessionId as string) || v4());
  const [openImageCarousel, setOpenImageCarousel] = useState(false);
  const shouldSaveSession = !router.query.sessionId;

  const defaultTrim = trimStyles.filter((t) => t.msrp > 0)[0] || trimStyles[0];
  const defaultImage = getDefaultImage(defaultTrim.exteriorImages);
  const [currentExtendedOptions, setCurrentExtendedOptions] =
    useState<ExtendedOptions>({});

  const reducer: ComponentReducer = (state, action) => {
    switch (action.type) {
      case SET_SELECTED_TRIM: {
        if (action.val.trimId === state.selectedTrim?.trimId) {
          return state;
        }

        return {
          ...state,
          selectedTrim: action.val,
          selectedExtendedOptions: [],
          selectedExteriorColor: getDefaultImage(action.val.exteriorImages),
          selectedInteriorColor: FallBackColor,
        };
      }

      case SELECT_EXTERIOR: {
        return {
          ...state,
          selectedExteriorColor: action.val,
        };
      }
      case SELECT_INTERIOR: {
        return {
          ...state,
          selectedInteriorColor: action.val,
        };
      }
      case SELECT_IMAGE: {
        return {
          ...state,
          selectedImage: action.val,
        };
      }
      case ADD_EXTENDED_OPTION: {
        const { description } = action.val;
        if (
          state.selectedExtendedOptions.find(
            (option) => option.description === description
          )
        ) {
          return state;
        }
        return {
          ...state,
          selectedExtendedOptions: [
            ...state.selectedExtendedOptions,
            action.val,
          ],
        };
      }
      case REMOVE_EXTENDED_OPTION: {
        return {
          ...state,
          selectedExtendedOptions: state.selectedExtendedOptions.filter(
            (option: ExtendedOptionsFields) =>
              option.description !== action.val.description
          ),
        };
      }
      case CLEAR_EXTENDED_OPTION: {
        return {
          ...state,
          selectedExtendedOptions: [],
        };
      }
      case SET_LOADING_IMAGE: {
        return {
          ...state,
          loadingImage: action.val,
        };
      }
      case SET_OPEN_INCENTIVES_OVERLAY: {
        return {
          ...state,
          openIncentivesOverlay: action.val,
        };
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState, (x) => {
    const buildImage = build?.image
      ? {
          href: build.image,
          shotCode: '03',
          shotDest: '',
          background: 'Transparent',
          width: 0,
          height: 0,
        }
      : undefined;
    const defaultExterior = buildImage
      ? {
          code: '',
          rgb: '',
          colorName: 'Build',
          msrp: 0,
          images: [buildImage],
        }
      : defaultImage;

    return {
      selectedTrim: x?.trim,
      selectedExteriorColor: x?.exteriorColor ?? defaultExterior,
      selectedInteriorColor: x?.interiorColor ?? FallBackColor,
      selectedImage: x?.exteriorColor?.images[0] ?? buildImage,
      selectedExtendedOptions: initialState?.extendedOptions || [],
      openIncentivesOverlay: false,
      loadingImage: false,
    };
  });

  const {
    selectedTrim,
    selectedExteriorColor,
    selectedInteriorColor,
    selectedImage,
    openIncentivesOverlay,
    loadingImage,
    selectedExtendedOptions,
  } = state;

  const updateQuery = useCallback(
    ({
      set,
      unset,
    }: {
      set?: Partial<TrimFormState>; // Enforce a single key-value pair
      unset?: (keyof TrimFormState)[]; // Ensure `unset` only includes valid keys
    }) => {
      const updatedQuery: Record<string, string | string[]> = {
        ...router.query,
        ...set,
      };

      if (unset) {
        unset.forEach((key) => {
          delete updatedQuery[key];
        });
      }

      router.replace(
        {
          pathname: router.pathname,
          query: updatedQuery,
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const setOpenIncentivesOverlay = (val: boolean) => {
    dispatch({
      type: SET_OPEN_INCENTIVES_OVERLAY,
      val,
    });
  };

  const setSelectedTrim = useCallback(
    (val: TrimStyle) => {
      updateQuery({
        set: { trim: val.trim },
        unset: [
          'exteriorName',
          'interiorColorName',
          'extendOptionDescriptions',
        ],
      });
      dispatch({
        type: SET_SELECTED_TRIM,
        val,
      });
    },
    [updateQuery]
  );

  const setLoadingImage = (val: boolean) => {
    dispatch({
      type: SET_LOADING_IMAGE,
      val,
    });
  };

  const setSelectedImage = (val: ImageObject['images'][0] | undefined) => {
    dispatch({
      type: SELECT_IMAGE,
      val,
    });
  };

  const setSelectedExteriorColor = (val: ImageObject) => {
    dispatch({
      type: SELECT_EXTERIOR,
      val,
    });
    const newImage = val.images[0];
    if (
      newImage &&
      newImage?.shotCode &&
      newImage.shotCode === selectedImage?.shotCode &&
      newImage.href !== selectedImage?.href
    ) {
      setSelectedImage(newImage);
    }
    updateQuery({
      set: { exteriorName: val.colorName },
    });
  };

  const setSelectedInteriorColor = (val: ColorObject) => {
    dispatch({
      type: SELECT_INTERIOR,
      val,
    });
    updateQuery({
      set: { interiorColorName: val.colorName },
    });
  };

  const setExtendedOption = (val: ExtendedOptionsFields) => {
    dispatch({
      type: ADD_EXTENDED_OPTION,
      val,
    });
    updateQuery({
      set: {
        extendOptionDescriptions: [...selectedExtendedOptions, val].map(
          (option) => option.description
        ),
      },
    });
  };

  const removeExtendedOption = (val: ExtendedOptionsFields) => {
    const { description } = val;
    dispatch({
      type: REMOVE_EXTENDED_OPTION,
      val,
    });
    updateQuery({
      set: {
        extendOptionDescriptions: selectedExtendedOptions
          .filter((option) => option.description !== description)
          .map((option) => option.description),
      },
    });
  };

  const handleCloseIncentivesOverlay = () => {
    setOpenIncentivesOverlay(false);
  };

  const handleOpenIncentivesOverlay = () => {
    setOpenIncentivesOverlay(true);
  };

  const trim = selectedTrim || defaultTrim;

  const concatedImages = [
    ...(selectedExteriorColor.images.length > 0
      ? selectedExteriorColor.images
      : defaultImage.images),
    ...trim.interiorImages,
  ];

  const selectedTrimId = selectedTrim?.trimId || defaultTrim.trimId;
  const displayImage = selectedImage ||
    selectedExteriorColor?.images?.[0] || {
      href: '',
      shotDest: 'main image',
    };

  const allExteriors: ImageObject[] = [
    getDefaultImage(selectedTrim?.exteriorImages || defaultTrim.exteriorImages),
    ...(selectedTrim?.exteriorImages || []),
  ].filter((image) => image.colorName !== 'Unknown');

  const allInteriors: ColorObject[] = [
    FallBackColor,
    ...(selectedTrim?.interiorColors || []),
  ];

  useEffect(() => {
    const getExtendedOptions = () => {
      const trimStyle = trimStyles.find(
        (option) => option.trimId === selectedTrimId
      );
      const { extendedOptions } = trimStyle || {};
      if (extendedOptions) {
        setCurrentExtendedOptions(extendedOptions);
      }
    };

    getExtendedOptions();
  }, [selectedTrimId, trimStyles]);

  Object.keys(currentExtendedOptions).forEach((category) => {
    currentExtendedOptions[category].forEach((option) => {
      if (option.mandatory) {
        setExtendedOption(option);
      }
    });
  });

  const extendedOptionsMSRP = selectedExtendedOptions.reduce(
    (accumulator: number, option: ExtendedOptionsFields) => {
      return accumulator + option.msrp;
    },
    0
  );

  const totalMSRP =
    trim.msrp +
    (selectedInteriorColor?.msrp || 0) +
    (selectedExteriorColor?.msrp || 0) +
    (extendedOptionsMSRP || 0);

  const { preferredZip: zip, loading: zipLoading } = useZipContext();

  const { incentivesData, incentivesLoading, incentivesFailed } =
    useFetchIncentives({
      zip,
      zipLoading,
      styleId: selectedTrim?.trimId,
    });

  const incentiveTotal =
    !incentivesLoading && !incentivesFailed && incentivesData
      ? incentivesData.incentivesAmount + incentivesData.rebateAmount
      : 0;
  const range = getRange(trim);
  const header = (
    <div className="z-100 flex max-w-full flex-wrap items-start justify-between gap-2.5 border-b border-gray-300 bg-white py-5">
      <div className="inline-flex w-full flex-col items-start justify-center gap-0.5 md:w-auto">
        <div className="text-subtitle14px font-light md:text-subtitle16px">
          {selectedTrim?.year || defaultTrim.year} ·{' '}
          {selectedTrim?.make || defaultTrim.make}
        </div>
        <div className="text-subtitle14px !font-medium md:text-title20px">
          {trim.model}
        </div>
      </div>

      <div className="flex h-[50px] w-full items-start justify-between gap-[20px] md:w-auto md:justify-start md:gap-[30px]">
        <div className="inline-flex flex-col items-start justify-start gap-0.5">
          <div className="text-subtitle14px font-light md:text-subtitle16px">
            Est. Range
          </div>
          <div>
            <span className="pr-[0.25rem] text-subtitle14px !font-medium md:text-subtitle16px">
              {range?.value}
            </span>
            <span className="text-subtitle14px font-light md:text-subtitle16px">
              {range?.unit}
            </span>
          </div>
        </div>

        <div className="h-full w-0 border-r border-gray-300"></div>

        <div className="inline-flex flex-col items-start justify-start gap-0.5">
          <div className="flex h-full flex-col items-start justify-start gap-0.5">
            <div className="text-subtitle14px font-light md:text-subtitle16px">
              Est. MSRP
            </div>
            <div className="text-subtitle14px !font-medium md:text-subtitle16px">
              {priceFormatter(totalMSRP, PRICE_COMING_SOON)}
            </div>
          </div>
        </div>
        {process.env.NEXT_PUBLIC_ENABLE_INCENTIVES_REBATES === 'true' && (
          <div className="inline-flex flex-col items-start justify-start gap-0.5">
            <div className="flex h-full flex-col items-start justify-start gap-0.5">
              <div className="text-subtitle14px font-light md:text-subtitle16px">
                Est. Incentives
              </div>
              <div className="flex flex-row items-center gap-xs text-subtitle14px !font-medium md:text-subtitle16px">
                {!incentivesData && !selectedTrim?.trimId && 'Select Trim'}
                {incentivesLoading &&
                  selectedTrim?.trimId &&
                  'Loading Incentives'}
                {incentivesFailed &&
                  selectedTrim?.trimId &&
                  'Failed to fetch incentives'}
                {incentivesData && !incentivesLoading && (
                  <>
                    ${incentiveTotal.toLocaleString()}
                    <div
                      className="m-auto cursor-pointer"
                      onClick={handleOpenIncentivesOverlay}
                    >
                      <InformationCircleIcon className="size-[20px] stroke-2 text-brandMidnight" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const saveUserSession = useCallback(async () => {
    if (shouldSaveSession) {
      await saveSession({
        id: sessionId,
        year: defaultTrim.year,
        make: defaultTrim.make,
        model: defaultTrim.model,
        image: displayImage.href,
        trim: defaultTrim.trim,
        price: `$${totalMSRP.toLocaleString()}`,
        url: router.asPath,
      });
    }
  }, [
    sessionId,
    totalMSRP,
    defaultTrim,
    saveSession,
    displayImage.href,
    shouldSaveSession,
    router,
  ]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        await saveUserSession();
      }
    };

    const handleRouteChangeStart = async (nextUrl: string) => {
      // Current path, e.g. '/build/NY/honda/2020/civic?foo=bar'
      const currentPath = router.asPath;

      // Remove query params for base comparison
      const stripQuery = (url: string) => url.split('?')[0];

      const currentBase = stripQuery(currentPath);
      const nextBase = stripQuery(nextUrl);

      // If nextBase is exactly the same or is currentBase + '/review',
      // skip saving session, otherwise proceed
      if (nextBase === currentBase || nextBase === `${currentBase}/review`) {
        return;
      }

      await saveUserSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', saveUserSession);
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', saveUserSession);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [saveUserSession, router]);

  return (
    <div className={'relative'}>
      {/* sticky */}

      {openIncentivesOverlay && (
        <IncentivesOverlay
          incentives={incentivesData?.all || []}
          incentiveAmount={incentiveTotal}
          open={openIncentivesOverlay}
          close={handleCloseIncentivesOverlay}
        />
      )}

      <div className={'sticky top-0 z-10 flex w-full flex-col pb-[40px]'}>
        {header}
      </div>

      <div className="flex w-full flex-col gap-[40px] md:flex-row">
        <div className="h-auto md:min-h-[400px] md:w-6/12">
          <div className="top-[120px] flex flex-col md:sticky">
            <div
              className={clsx(
                'h-content relative flex w-full items-center justify-center p-[10px]',
                {
                  'animate-pulse': loadingImage,
                  'cursor-pointer': concatedImages.length > 0,
                }
              )}
              onClick={() => {
                if (concatedImages.length > 0) {
                  setOpenImageCarousel(true);
                }
              }}
            >
              {displayImage?.href ? (
                <Image
                  priority
                  width={400}
                  height={400}
                  quality={100}
                  className={'!h-auto w-full'}
                  key={displayImage?.href}
                  src={displayImage?.href}
                  alt={displayImage?.shotDest || 'main image'}
                  loading={'eager'}
                  onLoad={() => {
                    setLoadingImage(false);
                  }}
                />
              ) : (
                <div className="flex h-[300px] w-full bg-gradient-to-r from-lightTheme-start to-lightTheme-end">
                  <NoImageWrapper
                    className="m-auto mb-s flex-col items-center justify-center text-blue-dark opacity-40"
                    makeIconClassName="size-[64px]"
                    make={defaultTrim.make}
                  />
                </div>
              )}
            </div>
            <div className="relative flex w-full flex-wrap">
              {concatedImages.slice(0, 3).map((image, index) => (
                <div
                  key={index}
                  className={`w-4/12 cursor-pointer rounded border-2 p-1 px-1 ${
                    image.shotCode === displayImage?.shotCode
                      ? 'border-gray'
                      : 'border-white'
                  }`}
                  onClick={() => {
                    setSelectedImage(image);
                  }}
                >
                  <Image
                    height={200}
                    width={200}
                    className={'!h-auto w-full'}
                    key={image.href}
                    src={image.href}
                    alt={image.shotDest || 'exterior'}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative flex w-full flex-col overflow-hidden md:w-6/12">
          <CollapsibleSection
            title={'Trims'}
            renderSelection={
              <div className="flex justify-between text-base font-light leading-snug tracking-tight text-neutral-800">
                <div>{trim.trim}</div>
                <div>{priceFormatter(trim.msrp, PRICE_COMING_SOON)}</div>
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              {trimStyles.map((thisTrim) => (
                <TrimCard
                  trim={thisTrim}
                  key={thisTrim.trimId}
                  selected={thisTrim.trimId === selectedTrim?.trimId}
                  onClick={() => {
                    setSelectedTrim(thisTrim);
                  }}
                />
              ))}
            </div>
          </CollapsibleSection>

          {selectedTrim && (
            <CollapsibleSection
              title={'Exterior'}
              showResult
              className={`transition-opacity delay-500 duration-500`}
              renderSelection={
                <div className="flex justify-between text-base font-light leading-snug tracking-tight text-neutral-800">
                  <div>Color - {selectedExteriorColor.colorName}</div>
                  <div>{priceFormatter(selectedExteriorColor.msrp)}</div>
                </div>
              }
            >
              <div className="flex h-full w-full flex-wrap space-y-2 p-2">
                {allExteriors.map((image, index) => (
                  <ColorOption
                    key={index}
                    selected={
                      image.colorName === selectedExteriorColor?.colorName &&
                      image.code === selectedExteriorColor.code
                    }
                    {...image}
                    handleOnClick={() => {
                      if (selectedExteriorColor.code !== image.code) {
                        if (selectedExteriorColor.images.length > 0) {
                          setLoadingImage(true);
                        }
                      }
                      setSelectedExteriorColor(image);
                      setSelectedImage(image.images[0]);
                      setLoadingImage(false);
                    }}
                  />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {selectedTrim && (
            <CollapsibleSection
              title={'Interior Color'}
              className={`transition-opacity delay-500 duration-500`}
              renderSelection={
                <div className="flex justify-between text-base font-light leading-snug tracking-tight text-neutral-800">
                  <div>Color - {selectedInteriorColor.colorName}</div>
                  <div>
                    {selectedInteriorColor.msrp > 0
                      ? `$${selectedInteriorColor.msrp.toLocaleString()}`
                      : 'Included'}
                  </div>
                </div>
              }
            >
              <div className="flex flex-col gap-2">
                {allInteriors.map((color) => (
                  <ColorCard
                    key={color.colorName}
                    color={color}
                    selected={
                      color.colorName === selectedInteriorColor?.colorName
                    }
                    handleOnClick={setSelectedInteriorColor}
                  />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {selectedTrim && Object.keys(currentExtendedOptions).length > 0 && (
            <CollapsibleSection
              title={POPULAR_OPTIONS}
              className={`transition-opacity delay-500 duration-500`}
            >
              <div className="relative flex max-h-[500px] flex-col gap-2 overflow-y-auto">
                {Object.entries(currentExtendedOptions).map(
                  ([category, options]) => {
                    const title = category.replaceAll('_', ' ');
                    return (
                      <div key={title}>
                        <div className="mb-[10px]">{title}</div>
                        {options.map((option: ExtendedOptionsFields, i) => {
                          return (
                            <ExtendedOptionCard
                              option={option}
                              key={`${option.code}-${category}-${selectedTrimId}-${i}`}
                              handleAddDispatch={setExtendedOption}
                              handleRemoveDispatch={removeExtendedOption}
                              isSelected={selectedExtendedOptions.some(
                                ({ description }) =>
                                  description === option.description
                              )}
                            />
                          );
                        })}
                      </div>
                    );
                  }
                )}
              </div>
            </CollapsibleSection>
          )}

          <div className={'mt-l flex w-full flex-col justify-end'}>
            <Button
              variant={ButtonVariants.Primary}
              disabled={
                !selectedInteriorColor ||
                !selectedExteriorColor ||
                !selectedTrim
              }
              analyticsEvent={GOOGLE_TRACK_INFO.reviewEVBuildButton}
              onClick={() => {
                router.push({
                  pathname: '/build/[location]/[make]/[year]/[model]/review',
                  query: {
                    location: router.query.location,
                    make: router.query.make,
                    year: router.query.year,
                    model: router.query.model,
                    sessionId,
                    trim: defaultTrim.trim,
                    exteriorName: selectedExteriorColor.colorName,
                    interiorColorName: selectedInteriorColor.colorName,
                    extendOptionDescriptions: selectedExtendedOptions.map(
                      (option) => option.description
                    ),
                  },
                });
              }}
              aria-label="review build"
            >
              Review This Build
            </Button>
          </div>
        </div>
      </div>
      <ImageCarouselPopup
        open={openImageCarousel}
        navCarousel={concatedImages.length > 2}
        handleClose={() => {
          setOpenImageCarousel(false);
        }}
        images={concatedImages.map((image) => image.href)}
      />
      <div className="sticky" />
    </div>
  );
};
